import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import type { NavigationProp } from '@react-navigation/native';
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  Modal,
  Alert,
  ActivityIndicator,
  ScrollView
} from 'react-native';
import tw from 'twrnc';

import { ENV_CONFIG } from '../../../config/environment';
import { useGuestUser } from '../../../shared/contexts/GuestUserContext';
import { useAuth } from '../../../shared/hooks/useAuth';
import { freemiumLimitService, FreemiumLimits } from '../services/freemiumLimitService';
import { guestLimitService } from '../services/guestLimitService';
import { PremiumFeature } from '../types/premiumFeatures';

import { PremiumFeatureGate } from './PremiumFeatureGate';
import { uiText } from '../../../shared/utils/uiText';

interface FreemiumQuizGateProps {
  children: React.ReactNode;
  onQuizStart?: () => void;
  onQuizComplete?: () => void;
  disabled?: boolean;
}

export const FreemiumQuizGate: React.FC<FreemiumQuizGateProps> = ({
  children,
  onQuizStart,
  onQuizComplete,
  disabled = false
}) => {
  const [limits, setLimits] = useState<FreemiumLimits | null>(null);
  const [loading, setLoading] = useState(true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [hasRecordedCompletion, setHasRecordedCompletion] = useState(false);
  const [hasNavigatedToUpgrade, setHasNavigatedToUpgrade] = useState(false);
  const { user } = useAuth();
  const { isGuest } = useGuestUser();
  const navigation = useNavigation<NavigationProp<any>>();

  useEffect(() => {
    checkDailyLimits();
  }, [user, isGuest]);

  // Monitor quiz completion by watching children props
  useEffect(() => {
    console.log('[FreemiumQuizGate] Monitoring quiz completion...');

    // Check if quiz has finished by examining children props
    if (React.isValidElement(children)) {
      const childProps = children.props as { isGameFinished?: boolean };
      console.log('[FreemiumQuizGate] Child props:', {
        isGameFinished: childProps?.isGameFinished,
        hasUser: !!user?.id,
        isGuest: isGuest,
        hasRecorded: hasRecordedCompletion
      });

      // Check if quiz is finished (from UnifiedQuizRenderer) and we haven't recorded yet
      // FIXED: Now works for BOTH authenticated users AND guest users
      if (childProps?.isGameFinished && (user?.id || isGuest) && !hasRecordedCompletion) {
        console.log('[FreemiumQuizGate] Quiz finished detected, recording completion for', isGuest ? 'guest' : 'authenticated user');

        setHasRecordedCompletion(true);

        // Record quiz completion
        handleQuizComplete().then(() => {
          console.log('[FreemiumQuizGate] Quiz completion recorded successfully');
        }).catch((error) => {
          console.error('[FreemiumQuizGate] Failed to record quiz completion:', error);
        });
      }
    }
  }, [children, user?.id, isGuest, hasRecordedCompletion]);

  // Check if user has reached daily limit - in useEffect to avoid infinite renders
  useEffect(() => {
    if (limits && !limits.hasUnlimited && limits.remainingQuizzes <= 0 && !showUpgradeModal && !hasNavigatedToUpgrade) {
      setShowUpgradeModal(true);
    }
  }, [limits?.remainingQuizzes, limits?.hasUnlimited, showUpgradeModal, hasNavigatedToUpgrade]);

  const checkDailyLimits = async () => {
    console.log('[FreemiumQuizGate] Starting checkDailyLimits');
    console.log('[FreemiumQuizGate] User ID:', user?.id);
    console.log('[FreemiumQuizGate] Is Guest:', isGuest);
    console.log('[FreemiumQuizGate] MONETIZATION_ENABLED:', ENV_CONFIG.FEATURES.MONETIZATION_ENABLED);

    // Handle guest users with guest limit service
    if (isGuest || !user?.id) {
      console.log('[FreemiumQuizGate] Guest user detected - checking guest limits');
      try {
        const guestUsage = await guestLimitService.getGuestDailyUsage();
        console.log('[FreemiumQuizGate] Guest usage:', guestUsage);
        setLimits({
          dailyQuizLimit: guestUsage.dailyQuizLimit,
          currentUsage: guestUsage.currentUsage,
          remainingQuizzes: guestUsage.remainingQuizzes,
          hasUnlimited: false,
          resetTime: guestUsage.resetTime
        });
      } catch (error) {
        console.error('[FreemiumQuizGate] Error getting guest limits:', error);
        // Fallback to default
        setLimits({
          dailyQuizLimit: 7,
          currentUsage: 0,
          remainingQuizzes: 7,
          hasUnlimited: false,
          resetTime: new Date()
        });
      }
      setLoading(false);
      return;
    }

    try {
      // If monetization is disabled globally, allow unlimited access
      if (!ENV_CONFIG.FEATURES.MONETIZATION_ENABLED) {
        console.log('[FreemiumQuizGate] Monetization disabled - setting unlimited');
        setLimits({
          dailyQuizLimit: -1,
          currentUsage: 0,
          remainingQuizzes: -1,
          hasUnlimited: true,
          resetTime: new Date()
        });
        setLoading(false);
        return;
      }

      const userLimits = await freemiumLimitService.getDailyUsage(user.id);
      console.log('[FreemiumQuizGate] User limits:', userLimits);
      console.log('[FreemiumQuizGate] Status bar should show:',
        !userLimits.hasUnlimited && userLimits.dailyQuizLimit > 0 && userLimits.remainingQuizzes <= 3);
      setLimits(userLimits);
    } catch (error) {
      console.error('Failed to check daily limits:', error);
      // Default to limited access on error (fail closed, not open)
      setLimits({
        dailyQuizLimit: 3,
        currentUsage: 0,
        remainingQuizzes: 3,
        hasUnlimited: false,
        resetTime: new Date()
      });
    } finally {
      setLoading(false);
    }
  };

  const handleQuizStart = async () => {
    if (!user?.id || !limits) {
      return;
    }

    // Check if user can start quiz
    const canStart = await freemiumLimitService.canStartQuiz(user.id);
    
    if (!canStart && !limits.hasUnlimited) {
      setShowUpgradeModal(true);
      return;
    }

    // Start the quiz
    onQuizStart?.();
  };

  const handleQuizComplete = async () => {
    try {
      // Record completion for guest users
      if (isGuest || !user?.id) {
        console.log('[FreemiumQuizGate] Recording guest quiz completion');
        await guestLimitService.recordGuestQuizCompletion();

        // Refresh guest limits
        await checkDailyLimits();

        onQuizComplete?.();
        return;
      }

      // Record completion for authenticated users
      console.log('[FreemiumQuizGate] Recording authenticated user quiz completion');
      await freemiumLimitService.recordQuizCompletion(user.id);

      // Refresh limits
      await checkDailyLimits();

      onQuizComplete?.();
    } catch (error) {
      console.error('Failed to record quiz completion:', error);
      onQuizComplete?.();
    }
  };

  const handleUpgrade = () => {
    console.log('[FreemiumQuizGate] Upgrade button clicked');
    setShowUpgradeModal(false);
    setHasNavigatedToUpgrade(true); // Prevent modal from showing again

    try {
      // Navigate to subscription plans screen
      console.log('[FreemiumQuizGate] Navigating to SubscriptionPlans');
      navigation.navigate('SubscriptionPlans' as never);
    } catch (error) {
      console.error('[FreemiumQuizGate] Navigation error:', error);
      // Fallback - could navigate to a different screen or show error
      console.log('[FreemiumQuizGate] Navigation failed, modal will close');
    }
  };

  if (loading) {
    return (
      <View style={tw`flex-1 items-center justify-center`}>
        <ActivityIndicator size="large" color="#4f46e5" />
        <Text style={tw`mt-2 text-gray-600`}>{uiText('loadingQuiz')}</Text>
      </View>
    );
  }

  // This should never happen now, but if it does, allow unlimited access
  if (!limits) {
    const defaultLimits = {
      dailyQuizLimit: -1,
      currentUsage: 0,
      remainingQuizzes: -1,
      hasUnlimited: true,
      resetTime: new Date()
    };
    setLimits(defaultLimits);
    // Just render children without modification
    return <View style={tw`flex-1`}>{children}</View>;
  }

  // Block quiz access when limit is reached
  if (!limits.hasUnlimited && limits.remainingQuizzes <= 0) {
    return (
      <View style={tw`flex-1`}>
        {/* Warm status bar */}
        <View style={tw`bg-indigo-50 border-b border-indigo-100 px-4 py-2`}>
          <View style={tw`flex-row items-center justify-between`}>
            <View style={tw`flex-row items-center flex-1`}>
              <Ionicons name="book-outline" size={16} color="#6366f1" />
              <Text style={tw`ml-2 text-indigo-700 text-sm`}>
                Great session today! More quizzes at {limits.resetTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </Text>
            </View>

            <TouchableOpacity
              style={tw`bg-indigo-500 px-3 py-1 rounded-full`}
              onPress={handleUpgrade}
            >
              <Text style={tw`text-white text-xs font-medium`}>
                Go Deeper
              </Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* Invitation content area */}
        <View style={tw`flex-1 items-center justify-center p-6`}>
          <View style={tw`items-center mb-6`}>
            <View style={tw`w-20 h-20 bg-indigo-50 rounded-full items-center justify-center mb-4`}>
              <Ionicons name="book" size={40} color="#6366f1" />
            </View>
            <Text style={tw`text-2xl font-bold text-gray-900 text-center mb-2`}>
              What a day of learning!
            </Text>
            <Text style={tw`text-gray-600 text-center mb-4`}>
              You've completed {limits.currentUsage} quizzes today — wonderful!
            </Text>
            <Text style={tw`text-gray-700 text-center font-medium mb-8`}>
              Come back tomorrow for more, or unlock unlimited access to keep going deeper.
            </Text>
          </View>

          {/* Benefits */}
          <View style={tw`w-full mb-8`}>
            <View style={tw`flex-row items-center mb-3`}>
              <Ionicons name="checkmark-circle" size={24} color="#6366f1" />
              <Text style={tw`ml-3 text-gray-700 text-lg`}>{uiText('gateUnlimited')}</Text>
            </View>
            <View style={tw`flex-row items-center mb-3`}>
              <Ionicons name="checkmark-circle" size={24} color="#6366f1" />
              <Text style={tw`ml-3 text-gray-700 text-lg`}>{uiText('gateFamilyPlay')}</Text>
            </View>
            <View style={tw`flex-row items-center mb-3`}>
              <Ionicons name="checkmark-circle" size={24} color="#6366f1" />
              <Text style={tw`ml-3 text-gray-700 text-lg`}>{uiText('gateInsights')}</Text>
            </View>
            <View style={tw`flex-row items-center mb-3`}>
              <Ionicons name="checkmark-circle" size={24} color="#6366f1" />
              <Text style={tw`ml-3 text-gray-700 text-lg`}>{uiText('gateFormats')}</Text>
            </View>
          </View>

          {/* Invitation button */}
          <TouchableOpacity
            style={tw`bg-indigo-500 py-4 px-8 rounded-xl w-full`}
            onPress={handleUpgrade}
          >
            <Text style={tw`text-white text-center font-bold text-lg`}>
              Explore Premium
            </Text>
          </TouchableOpacity>

          {/* Reset time - warm tone */}
          <Text style={tw`text-sm text-gray-500 text-center mt-4`}>
            Your next free quizzes are ready at {limits.resetTime.toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit'
            })}
          </Text>
        </View>
      </View>
    );
  }

  // Show upgrade modal when user hasn't hit limit yet but modal was triggered
  if (showUpgradeModal) {
    return (
      <Modal
        visible={true}
        transparent={true}
        animationType="slide"
      >
        <View style={tw`flex-1 bg-black bg-opacity-50 items-center justify-center p-4`}>
          <View style={tw`bg-white rounded-2xl w-full max-w-sm max-h-[90%]`}>
            <ScrollView
              showsVerticalScrollIndicator={false}
              bounces={false}
              contentContainerStyle={tw`p-6`}
            >
              {/* Header */}
              <View style={tw`items-center mb-4`}>
                <View style={tw`w-16 h-16 bg-indigo-50 rounded-full items-center justify-center mb-3`}>
                  <Ionicons name="book" size={32} color="#6366f1" />
                </View>
                <Text style={tw`text-xl font-bold text-gray-900 text-center`}>
                  Great time in the Word!
                </Text>
              </View>

              {/* Message */}
              <Text style={tw`text-gray-600 text-center mb-2`}>
                You've completed {limits.currentUsage} quizzes today — wonderful!
              </Text>
              <Text style={tw`text-gray-700 text-center font-medium mb-6`}>
                Come back tomorrow, or unlock unlimited access to go deeper.
              </Text>

              {/* Benefits */}
              <View style={tw`mb-6`}>
                <View style={tw`flex-row items-center mb-2`}>
                  <Ionicons name="checkmark-circle" size={20} color="#6366f1" />
                  <Text style={tw`ml-2 text-gray-700`}>{uiText('gateUnlimited')}</Text>
                </View>
                <View style={tw`flex-row items-center mb-2`}>
                  <Ionicons name="checkmark-circle" size={20} color="#6366f1" />
                  <Text style={tw`ml-2 text-gray-700`}>{uiText('gateFamilyGrowth')}</Text>
                </View>
                <View style={tw`flex-row items-center mb-2`}>
                  <Ionicons name="checkmark-circle" size={20} color="#6366f1" />
                  <Text style={tw`ml-2 text-gray-700`}>{uiText('gateInsights')}</Text>
                </View>
                <View style={tw`flex-row items-center mb-2`}>
                  <Ionicons name="checkmark-circle" size={20} color="#6366f1" />
                  <Text style={tw`ml-2 text-gray-700`}>{uiText('gateFormats')}</Text>
                </View>
              </View>

              {/* Buttons */}
              <View style={tw`flex-row justify-between`}>
                <TouchableOpacity
                  style={tw`flex-1 bg-gray-100 py-3 px-4 rounded-lg mr-2`}
                  onPress={() => {
                    setShowUpgradeModal(false);
                    // Navigate back to prevent playing more quizzes
                    if (limits && limits.remainingQuizzes <= 0) {
                      console.log('[FreemiumQuizGate] Limit reached - navigating back');
                      navigation.goBack();
                    }
                  }}
                >
                  <Text style={tw`text-gray-700 text-center font-medium`}>
                    {limits && limits.remainingQuizzes <= 0 ? 'See You Tomorrow' : 'Maybe Later'}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={tw`flex-1 bg-indigo-500 py-3 px-4 rounded-lg ml-2`}
                  onPress={handleUpgrade}
                >
                  <Text style={tw`text-white text-center font-medium`}>
                    Explore Premium
                  </Text>
                </TouchableOpacity>
              </View>

              {/* Reset time */}
              <Text style={tw`text-xs text-gray-500 text-center mt-3`}>
                More free quizzes at {limits.resetTime.toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </Text>
            </ScrollView>
          </View>
        </View>
      </Modal>
    );
  }

  // No status bar - users play freely until limit is reached
  // When limit is reached (remainingQuizzes <= 0), the blocking screen above handles it
  return (
    <View style={tw`flex-1`}>
      {/* Quiz content - render without any limit notifications */}
      {children}
    </View>
  );
};

export default FreemiumQuizGate;